import { Time } from '../core/Time';
import { Config } from '../core/Config';
import { Camera } from '../rendering/Camera';
import { Scene } from '../scene/Scene';
import { LODSystem } from '../optimization/LODSystem';
import { CullingManager } from '../optimization/CullingManager';
import { Engine } from '../core/Engine';

export class DebugUI {
  private element: HTMLElement;
  private time: Time;
  private camera: Camera;
  private scene: Scene;
  private keyPressListened: boolean = false;
  private lodRangeSliders: {
    high: HTMLInputElement;
    medium: HTMLInputElement;
  } | null = null;
  
  constructor(time: Time, camera: Camera, scene: Scene) {
    this.time = time;
    this.camera = camera;
    this.scene = scene;
    this.element = document.getElementById('debug-panel') as HTMLElement;
    
    if (!this.element) {
      console.error('Debug panel element not found');
      return;
    }
    
    if (!Config.SHOW_DEBUG_INFO) {
      this.element.style.display = 'none';
      return; // don't show debug info in production
    }
    
    // Set up key listener for toggling quadtree visualization
    this.setupKeyListener();
    
    // Initialize with default quadtree visualization setting
    if (Config.QUADTREE_DEBUG_ENABLED) {
      this.scene.getObjectManager().toggleSpatialDebug();
    }
    
    // Add LOD control UI
    this.createLODControls();
    
    // Add instructions for pathfinding toasters
    this.createPathfindingInfo();
    
    console.log('Debug UI initialized');
  }
  
  private setupKeyListener(): void {
    if (this.keyPressListened) return;
    
    window.addEventListener('keydown', (e) => {
      // Toggle quadtree visualization with 'Q' key
      if (e.key === 'q' || e.key === 'Q') {
        this.scene.getObjectManager().toggleSpatialDebug();
      }
      
      // Perform spatial query with 'E' key
      if (e.key === 'e' || e.key === 'E') {
        console.log('E key pressed - running spatial query');
        this.scene.performSpatialQuery();
      }
      
      // Toggle LOD debug info with 'L' key
      if (e.key === 'l' || e.key === 'L') {
        const lodControlsElement = document.getElementById('lod-controls');
        if (lodControlsElement) {
          lodControlsElement.style.display = 
            lodControlsElement.style.display === 'none' ? 'block' : 'none';
        }
      }
      
      // // Toggle culling debug visualization with 'C' key
      if (e.key === 'c' || e.key === 'C') {
        const cullingManager = this.scene.getCullingManager();
        if (cullingManager) {
          cullingManager.toggleDebugMode();
          console.log('Culling debug visualization toggled');
        }
      }
      
      // Toggle frustum culling with 'F' key
      if (e.key === 'f' || e.key === 'F') {
        const cullingManager = this.scene.getCullingManager();
        if (cullingManager) {
          // Toggle frustum culling in the culling manager
          const currentlyEnabled = (cullingManager as any)['frustumCullingEnabled'];
          cullingManager.setFrustumCullingEnabled(!currentlyEnabled);
          console.log(`Frustum culling ${!currentlyEnabled ? 'enabled' : 'disabled'}`);
        }
      }
      
      // Toggle occlusion culling with 'O' key
      if (e.key === 'o' || e.key === 'O') {
        const cullingManager = this.scene.getCullingManager();
        if (cullingManager) {
          const occlusionEnabled = !cullingManager.getOcclusionCullingSystem()['enabled'];
          cullingManager.setOcclusionCullingEnabled(occlusionEnabled);
          console.log(`Occlusion culling ${occlusionEnabled ? 'enabled' : 'disabled'}`);
        }
      }
      
      // Reset the game world with 'R' key
      if (e.key === 'r' || e.key === 'R') {
        console.log('R key pressed - resetting toasters');
        const scene = this.scene;
        
        // Reset the toasters and bread loaf
        scene.resetToasters();
        
        console.log('Toasters reset - new bread loaf placed and toasters respawned');
      }
    });
    
    this.keyPressListened = true;
  }
  
  private createPathfindingInfo(): void {
    // Create info panel for pathfinding
    const pathfindingInfoDiv = document.createElement('div');
    pathfindingInfoDiv.id = 'pathfinding-info';
    pathfindingInfoDiv.style.position = 'absolute';
    pathfindingInfoDiv.style.top = '10px';
    pathfindingInfoDiv.style.right = '10px';
    pathfindingInfoDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    pathfindingInfoDiv.style.color = 'white';
    pathfindingInfoDiv.style.padding = '10px';
    pathfindingInfoDiv.style.borderRadius = '5px';
    pathfindingInfoDiv.style.fontFamily = 'monospace';
    pathfindingInfoDiv.style.zIndex = '1000';
    
    // Add title and instructions
    pathfindingInfoDiv.innerHTML = `
      <h3 style="margin: 0 0 10px 0">Toaster Pathfinding</h3>
      <p>Red toasters are seeking the bread loaf</p>
      <p>Press R to reset the game world</p>
    `;
    
    // Add to the document
    document.body.appendChild(pathfindingInfoDiv);
  }
  
  private createLODControls(): void {
    // Create LOD controls container
    const lodControlsDiv = document.createElement('div');
    lodControlsDiv.id = 'lod-controls';
    lodControlsDiv.style.position = 'absolute';
    lodControlsDiv.style.bottom = '10px';
    lodControlsDiv.style.left = '10px';
    lodControlsDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    lodControlsDiv.style.color = 'white';
    lodControlsDiv.style.padding = '10px';
    lodControlsDiv.style.borderRadius = '5px';
    lodControlsDiv.style.fontFamily = 'monospace';
    lodControlsDiv.style.zIndex = '1000';
    
    // Add title
    const title = document.createElement('h3');
    title.textContent = 'LOD System Controls (Toggle with L key)';
    title.style.margin = '0 0 10px 0';
    lodControlsDiv.appendChild(title);
    
    // Get initial thresholds
    const lodSystem = this.scene.getLODSystem();
    const thresholds = lodSystem.getDistanceThresholds();
    
    // Create high detail slider
    const highDetailDiv = document.createElement('div');
    highDetailDiv.style.marginBottom = '10px';
    
    const highDetailLabel = document.createElement('label');
    highDetailLabel.textContent = 'High Detail Distance: ';
    highDetailLabel.setAttribute('for', 'high-detail-slider');
    highDetailDiv.appendChild(highDetailLabel);
    
    const highDetailValue = document.createElement('span');
    highDetailValue.id = 'high-detail-value';
    highDetailValue.textContent = thresholds.high.toString();
    highDetailDiv.appendChild(highDetailValue);
    
    const highDetailSlider = document.createElement('input');
    highDetailSlider.type = 'range';
    highDetailSlider.id = 'high-detail-slider';
    highDetailSlider.min = '5';
    highDetailSlider.max = '50';
    highDetailSlider.step = '1';
    highDetailSlider.value = thresholds.high.toString();
    highDetailSlider.style.width = '100%';
    
    highDetailSlider.addEventListener('input', () => {
      const highValue = parseInt(highDetailSlider.value);
      const mediumValue = parseInt(this.lodRangeSliders?.medium.value || thresholds.medium.toString());
      
      if (highValue >= mediumValue) {
        if (this.lodRangeSliders && this.lodRangeSliders.medium) {
          this.lodRangeSliders.medium.value = (highValue + 1).toString();
        }
        highDetailValue.textContent = highValue.toString();
        document.getElementById('medium-detail-value')!.textContent = (highValue + 1).toString();
        lodSystem.setDistanceThresholds(highValue, highValue + 1);
      } else {
        highDetailValue.textContent = highValue.toString();
        lodSystem.setDistanceThresholds(highValue, mediumValue);
      }
    });
    
    highDetailDiv.appendChild(highDetailSlider);
    lodControlsDiv.appendChild(highDetailDiv);
    
    // Create medium detail slider
    const mediumDetailDiv = document.createElement('div');
    
    const mediumDetailLabel = document.createElement('label');
    mediumDetailLabel.textContent = 'Medium Detail Distance: ';
    mediumDetailLabel.setAttribute('for', 'medium-detail-slider');
    mediumDetailDiv.appendChild(mediumDetailLabel);
    
    const mediumDetailValue = document.createElement('span');
    mediumDetailValue.id = 'medium-detail-value';
    mediumDetailValue.textContent = thresholds.medium.toString();
    mediumDetailDiv.appendChild(mediumDetailValue);
    
    const mediumDetailSlider = document.createElement('input');
    mediumDetailSlider.type = 'range';
    mediumDetailSlider.id = 'medium-detail-slider';
    mediumDetailSlider.min = '10';
    mediumDetailSlider.max = '100';
    mediumDetailSlider.step = '1';
    mediumDetailSlider.value = thresholds.medium.toString();
    mediumDetailSlider.style.width = '100%';
    
    mediumDetailSlider.addEventListener('input', () => {
      const mediumValue = parseInt(mediumDetailSlider.value);
      const highValue = parseInt(this.lodRangeSliders?.high.value || thresholds.high.toString());
      
      if (mediumValue <= highValue) {
        if (this.lodRangeSliders && this.lodRangeSliders.high) {
          this.lodRangeSliders.high.value = (mediumValue - 1).toString();
        }
        document.getElementById('high-detail-value')!.textContent = (mediumValue - 1).toString();
        mediumDetailValue.textContent = mediumValue.toString();
        lodSystem.setDistanceThresholds(mediumValue - 1, mediumValue);
      } else {
        mediumDetailValue.textContent = mediumValue.toString();
        lodSystem.setDistanceThresholds(highValue, mediumValue);
      }
    });
    
    mediumDetailDiv.appendChild(mediumDetailSlider);
    lodControlsDiv.appendChild(mediumDetailDiv);
    
    // Store sliders for later reference
    this.lodRangeSliders = {
      high: highDetailSlider,
      medium: mediumDetailSlider
    };
    
    // Add to the document
    document.body.appendChild(lodControlsDiv);
  }
  
  public update(): void {
    if (!this.element || !Config.SHOW_DEBUG_INFO) return;
    
    const cameraPosition = this.camera.getPosition();
    const objectManager = this.scene.getObjectManager();
    const spatialStats = objectManager.getSpatialStats();
    const lodSystem = this.scene.getLODSystem();
    const lodThresholds = lodSystem.getDistanceThresholds();
    const cullingManager = this.scene.getCullingManager();
    
    let html = '';
    
    if (Config.SHOW_FPS) {
      html += `FPS: ${this.time.fps}<br>`;
    }
    
    html += `
      Time: ${this.time.elapsedTime.toFixed(2)}s<br>
      Frame: ${this.time.frameCount}<br>
      <br>
      Camera Position:<br>
      X: ${cameraPosition.x.toFixed(2)}<br>
      Y: ${cameraPosition.y.toFixed(2)}<br>
      Z: ${cameraPosition.z.toFixed(2)}<br>
      <br>
      Objects: ${objectManager.getObjectCount()}<br>
      - Static: ${objectManager.getStaticObjectCount()}<br>
      - Dynamic: ${objectManager.getDynamicObjectCount()}<br>
      <br>
      Quadtree Stats:<br>
      - Nodes: ${spatialStats.nodes}<br>
      - Objects: ${spatialStats.objects}<br>
      - Max Depth: ${spatialStats.maxDepth}<br>
      <br>
      LOD Thresholds:<br>
      - High: ${lodThresholds.high}<br>
      - Medium: ${lodThresholds.medium}<br>
      <br>
      Controls:<br>
      - Q: Toggle Quadtree<br>
      - E: Spatial Query<br>
      - L: Toggle LOD Controls<br>
      - C: Toggle Culling Debug<br>
      - F: Toggle Frustum Culling<br>
      - O: Toggle Occlusion Culling<br>
      - R: Reset Game World<br>
    `;
    
    // Add culling stats if available
    if (cullingManager) {
      const cullingStats = cullingManager.getStats();
      html += `
        <br>
        Culling Stats:<br>
        - Visible: ${cullingStats.visibleObjects}<br>
        - Culled: ${cullingStats.objectsCulled}<br>
      `;
      
      // Add frustum culling stats if available
      if (cullingStats.frustumStats) {
        html += `- Frustum Culled: ${cullingStats.frustumStats.objectsChecked - cullingStats.frustumStats.objectsVisible}<br>`;
      }
      
      // Add occlusion culling stats if available
      if (cullingStats.occlusionStats) {
        html += `- Occlusion Culled: ${cullingStats.occlusionStats.objectsOccluded}<br>`;
      }
    }
    
    this.element.innerHTML = html;
  }
}
